/**
 * AnomalyAlertsMlDrivenUnusualActivityPatternsPage — alert feed for ML-driven
 * unusual activity patterns with severity badges + acknowledge action + filter.
 *
 * Palette constraint: GREEN accent (no emerald/blue/orange/purple/dark).
 */
import { useMemo, useState } from "react";
import {
	useAnomalyAlertsList,
	useAnomalyAlertAcknowledge,
} from "./hooks";
import type { AnomalyAlertRow, AnomalySeverity } from "./api";

const CARD =
	"rounded-[8px] border border-zinc-200 bg-white p-5 shadow-sm";
const INPUT =
	"rounded-[6px] border border-zinc-300 bg-white px-3 py-1.5 text-sm text-zinc-900 focus:border-green-500 focus:outline-none focus:ring-2 focus:ring-green-500/20";
const BTN =
	"rounded-[6px] border border-green-600 bg-green-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-green-700 disabled:cursor-not-allowed disabled:opacity-60";
const BTN_GHOST =
	"rounded-[6px] border border-zinc-300 bg-white px-2.5 py-1 text-xs font-medium text-zinc-700 hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-60";

const SEVERITY_ORDER: readonly AnomalySeverity[] = [
	"critical",
	"high",
	"medium",
	"low",
];

const SEVERITY_BADGE: Record<AnomalySeverity, string> = {
	low: "border-zinc-300 bg-zinc-50 text-zinc-700",
	medium: "border-yellow-400 bg-yellow-50 text-yellow-800",
	high: "border-red-400 bg-red-50 text-red-700",
	critical: "border-red-500 bg-red-50 text-red-800",
};

function SeverityBadge({ severity }: { severity: AnomalySeverity }) {
	const tone = SEVERITY_BADGE[severity] ?? SEVERITY_BADGE.low;
	return (
		<span
			className={`inline-flex items-center rounded-[6px] border px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide ${tone}`}
			data-testid={`anomaly-alerts-ml-driven-unusual-activity-patterns-severity-${severity}`}
		>
			{severity}
		</span>
	);
}

function formatTs(ts: number | null | undefined): string {
	if (!ts || !Number.isFinite(ts)) return "-";
	try {
		return new Date(ts * 1000).toISOString().replace("T", " ").slice(0, 19);
	} catch {
		return String(ts);
	}
}

function shortSignals(signals_json: string): string {
	try {
		const o = JSON.parse(signals_json) as Record<string, unknown>;
		const keys = Object.keys(o);
		if (keys.length === 0) return "(none)";
		return keys.slice(0, 4).join(", ");
	} catch {
		return "(invalid)";
	}
}

export function AnomalyAlertsMlDrivenUnusualActivityPatternsPage() {
	const [severity, setSeverity] = useState<AnomalySeverity | "">("");

	const listQuery = useAnomalyAlertsList({
		severity: severity || undefined,
	});
	const ackMut = useAnomalyAlertAcknowledge();

	const items: AnomalyAlertRow[] = useMemo(
		() => listQuery.data?.items ?? [],
		[listQuery.data],
	);

	return (
		<main
			data-testid="anomaly-alerts-ml-driven-unusual-activity-patterns-page"
			className="mx-auto flex w-full max-w-5xl flex-col gap-8 px-6 py-10"
		>
			<header className="flex flex-col gap-2">
				<p className="text-xs font-semibold uppercase tracking-wider text-zinc-500">
					anomaly alerts
				</p>
				<h1 className="text-3xl font-semibold text-green-700">
					ML-driven unusual activity
				</h1>
				<p className="text-sm text-zinc-600">
					Signals flagged by the anomaly detector. Acknowledge to mark
					reviewed.
				</p>
			</header>

			<section className={CARD} aria-label="Filter anomaly alerts">
				<div className="flex flex-wrap items-end gap-3">
					<label className="flex flex-col gap-1">
						<span className="text-xs font-medium text-zinc-600">
							Severity
						</span>
						<select
							className={INPUT}
							value={severity}
							onChange={(e) =>
								setSeverity(
									(e.currentTarget.value as AnomalySeverity | "") || "",
								)
							}
							data-testid="anomaly-alerts-ml-driven-unusual-activity-patterns-severity-filter"
						>
							<option value="">All severities</option>
							{SEVERITY_ORDER.map((s) => (
								<option key={s} value={s}>
									{s}
								</option>
							))}
						</select>
					</label>
					<button
						type="button"
						className={BTN_GHOST}
						onClick={() => setSeverity("")}
						disabled={!severity}
						data-testid="anomaly-alerts-ml-driven-unusual-activity-patterns-filter-clear"
					>
						Clear
					</button>
				</div>
			</section>

			<section
				className={CARD}
				aria-label="Anomaly alert feed"
			>
				<div className="flex items-baseline justify-between">
					<h2 className="text-base font-semibold text-zinc-900">
						Alert feed
					</h2>
					<span className="text-xs text-zinc-500">
						{items.length} shown
					</span>
				</div>

				{listQuery.isLoading ? (
					<p
						className="mt-3 text-sm text-zinc-500"
						data-testid="anomaly-alerts-ml-driven-unusual-activity-patterns-loading"
					>
						Loading.
					</p>
				) : listQuery.error ? (
					<p className="mt-3 text-sm text-red-600" role="alert">
						{listQuery.error.message}
					</p>
				) : items.length === 0 ? (
					<p
						className="mt-3 text-sm text-zinc-500"
						data-testid="anomaly-alerts-ml-driven-unusual-activity-patterns-empty"
					>
						No alerts match the current filter.
					</p>
				) : (
					<ul
						className="mt-3 divide-y divide-zinc-200"
						data-testid="anomaly-alerts-ml-driven-unusual-activity-patterns-list"
					>
						{items.map((row) => {
							const acked = row.status === "acknowledged";
							return (
								<li
									key={row.id}
									className="flex flex-col gap-2 py-3 sm:flex-row sm:items-center sm:justify-between"
									data-testid={`anomaly-alerts-ml-driven-unusual-activity-patterns-row-${row.alert_id}`}
								>
									<div className="flex flex-col gap-1">
										<div className="flex items-center gap-2">
											<SeverityBadge severity={row.severity} />
											<span className="font-mono text-xs text-zinc-700">
												{row.alert_id}
											</span>
											{acked ? (
												<span
													className="inline-flex items-center rounded-[6px] border border-green-400 bg-green-50 px-2 py-0.5 text-[11px] font-semibold uppercase text-green-800"
													data-testid={`anomaly-alerts-ml-driven-unusual-activity-patterns-status-acked-${row.alert_id}`}
												>
													acked
												</span>
											) : (
												<span className="inline-flex items-center rounded-[6px] border border-zinc-300 bg-zinc-50 px-2 py-0.5 text-[11px] font-semibold uppercase text-zinc-700">
													open
												</span>
											)}
										</div>
										<div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-zinc-600">
											<span>pattern: <span className="font-medium text-zinc-800">{row.pattern_type}</span></span>
											<span>score: <span className="font-medium text-zinc-800">{row.anomaly_score.toFixed(3)}</span></span>
											<span>entity: <span className="font-medium text-zinc-800">{row.entity_type}{row.entity_id ? `:${row.entity_id}` : ""}</span></span>
											<span>signals: <span className="font-medium text-zinc-800">{shortSignals(row.signals_json)}</span></span>
											<span>detected: {formatTs(row.detected_at)}</span>
											{acked ? (
												<span>acked: {formatTs(row.acknowledged_at)}</span>
											) : null}
										</div>
									</div>
									<div>
										<button
											type="button"
											className={BTN}
											onClick={() => ackMut.mutate(row.alert_id)}
											disabled={acked || ackMut.isPending}
											data-testid={`anomaly-alerts-ml-driven-unusual-activity-patterns-ack-${row.alert_id}`}
										>
											{acked
												? "Acknowledged"
												: ackMut.isPending && ackMut.variables === row.alert_id
													? "Saving."
													: "Acknowledge"}
										</button>
									</div>
								</li>
							);
						})}
					</ul>
				)}
				{ackMut.error ? (
					<p
						className="mt-3 text-xs text-red-600"
						role="alert"
						data-testid="anomaly-alerts-ml-driven-unusual-activity-patterns-ack-error"
					>
						{ackMut.error.message}
					</p>
				) : null}
			</section>
		</main>
	);
}

export default AnomalyAlertsMlDrivenUnusualActivityPatternsPage;
